import React, { type JSX } from "react";
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography
} from "@mui/material";
import ChevronDown from "../../../../../public/assets/icons/chevron-down.svg";
import theme from "../../../../theme/Theme";
import { AudienceConfig, DictionaryValue, TaskConfig } from "../../../../types/cqa";
import { Rubric, RubricVersion } from "../../../../types/rubric";
import Dropdown from "../../../atoms/dropdown/dropdown";
import "../ruleCommonSection.css";
import { ASTERISK_ELEMENT, CqaFormTextArea, CqaFormTextInput, DROPDOWN_STYLE, PLACEHOLDER_STYLE, SELECTION_COLOR } from "../ruleCommonStyleBlocks";
import { LABEL_TYPE_WORK_PROJECT, LABEL_TYPE_WORK_PROJECT_COMPARE, LABEL_TYPE_WORK_QUEUE } from "../constants";
import { ProjectConfiguration } from "../../../../types/sd";

interface Props {
    availableGuidelines?: string[];
    cqaConfig: ProjectConfiguration;
    description?: string;
    endDate?: string;
    guideline?: string;
    indefiniteTargetDate?: boolean;
    isCqaSample?: boolean;
    loading: boolean;
    rubricVersions?: RubricVersion[];
    rubrics?: Rubric[];
    ruleName: string;
    sampleReason?: string;
    sampleReasons?: DictionaryValue[];
    selectedRubricId?: number;
    selectedRubricVersionId?: number;
    startDate: string;
    onSampleReasonClick?: (e, value) => void;
    setCqaConfig: (value: React.SetStateAction<ProjectConfiguration>) => void;
    setDescription: (value: React.SetStateAction<string>) => void;
    setEndDate?: (value: string) => void;
    setGuideline?: React.Dispatch<string>;
    setIndefiniteTargetDate?: (value: boolean) => void;
    setName: (value: React.SetStateAction<string>) => void;
    setSelectedRubricId?: (value: number) => void;
    setSelectedRubricVersionId?: (value: React.SetStateAction<number>) => void;
    setStartDate: (value: string) => void;
}

export const Definition = ({
    availableGuidelines = [],
    cqaConfig,
    description = "",
    endDate = null,
    guideline = "",
    indefiniteTargetDate = true,
    isCqaSample = false,
    loading = false,
    rubricVersions = [],
    rubrics = [],
    ruleName,
    sampleReason = "",
    sampleReasons = [],
    selectedRubricId = null,
    selectedRubricVersionId = null,
    startDate,
    onSampleReasonClick = () => { },
    setCqaConfig = () => { },
    setDescription,
    setEndDate = () => { },
    setGuideline = () => { },
    setIndefiniteTargetDate = () => { },
    setName,
    setSelectedRubricId = () => { },
    setSelectedRubricVersionId = () => { },
    setStartDate,
}: Props): JSX.Element => {
    const audience = cqaConfig?.audience as AudienceConfig;
    const distributionExists = audience?.individuals && audience.individuals.length >= 1 && audience.individuals[0].label;
    const visibleRubricVersions = rubricVersions.filter((version) =>
        version.status === "PUBLISHED" || version.id === selectedRubricVersionId
    );

    const formatRubricVersionLabel = (version: RubricVersion): string => {
        const statusLabel = version.status === "PUBLISHED"
            ? ""
            : ` (${version.status.charAt(0)}${version.status.slice(1).toLowerCase()})`;
        const inactiveLabel = version.active === false ? " (Inactive)" : "";
        return `${version.majorVersion}.${version.minorVersion}${statusLabel}${inactiveLabel}`;
    };

    const onChangeTypeOfWork = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        const newConfig = { ...cqaConfig };
        newConfig["typeOfWork"] = value;
        setCqaConfig(newConfig);
    };

    return (
        <Box>
            {/* Container Title */}
            <Box className="setting_section_title_container">
                <Box className="setting_section_text_container" style={{ width: "76px" }}>
                    <span className="setting_section_text">
                        Definition
                    </span>
                </Box>
            </Box>
            {/* main area */}
            <Box
                id="project_definition"
                className='setting_section_main_container'
                sx={{ border: `1px solid ${theme.palette.artisticStroke200.main}`, minHeight: "432px" }}
            >
                {/* type of work */}
                {
                    isCqaSample && (
                        <Box>
                            <Box className="section_container">
                                <Typography className="setting_section_input_label">
                                    Type of work {ASTERISK_ELEMENT}
                                </Typography>
                            </Box>
                            <FormControl>
                                <RadioGroup
                                    data-testid="typeWorkRadioGroup"
                                    onChange={onChangeTypeOfWork}
                                    name="typeWorkRadioGroup"
                                    row
                                    sx={{ gap: "24px", flexWrap: "wrap" }}
                                    value={cqaConfig["typeOfWork"] || LABEL_TYPE_WORK_PROJECT}
                                >
                                    <FormControlLabel
                                        data-testid="radioLabelQueue"
                                        disabled={true}
                                        value={LABEL_TYPE_WORK_QUEUE}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Queue"
                                    />
                                    <FormControlLabel
                                        data-testid="radioLabelProject"
                                        disabled={!!distributionExists}
                                        value={LABEL_TYPE_WORK_PROJECT}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Project"
                                    />
                                    <FormControlLabel
                                        data-testid="radioLabelProjectCompare"
                                        disabled={!!distributionExists}
                                        value={LABEL_TYPE_WORK_PROJECT_COMPARE}
                                        control={<Radio sx={SELECTION_COLOR} />}
                                        label="Project (Compare Mode)"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )
                }
                {/* start date and end date */}
                <Box sx={{ width: "100%", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "12px 24px", angle: "0 deg", opacity: 1, }}>
                    <Box display="flex" flexDirection="column" sx={{ flex: "1 1 130px", minWidth: "130px", gap: "4px" }}>
                        <Typography className="setting_section_input_label">Start date {ASTERISK_ELEMENT}</Typography>
                        <input
                            aria-label="start-date-input"
                            style={{
                                border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                height: "32px",
                                width: "100%",
                                borderRadius: "3px",
                                padding: "8px",
                                boxSizing: "border-box",
                            }}
                            type="date"
                            id="start-date-picker"
                            value={startDate}
                            onChange={(e) => {
                                if (e.target.value?.length) {
                                    setStartDate(e.target.value)
                                } else {
                                    setStartDate("");
                                }
                            }}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column" sx={{ flex: "1 1 130px", minWidth: "130px", gap: "4px" }}>
                        <Typography className="setting_section_input_label">Target completion date {!indefiniteTargetDate ? ASTERISK_ELEMENT : null}</Typography>
                        <input
                            aria-label="end-date-input"
                            style={{
                                border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                height: "32px",
                                width: "100%",
                                borderRadius: "3px",
                                padding: "8px",
                                boxSizing: "border-box",
                            }}
                            type={!indefiniteTargetDate ? "date" : "text"}
                            id="end-date-picker"
                            min={startDate}
                            value={endDate !== "N/A" ? endDate : ""}
                            disabled={indefiniteTargetDate}
                            onChange={(e) => {
                                if (e.target.value?.length) {
                                    setEndDate(e.target.value)
                                } else {
                                    setEndDate("N/A");
                                }
                            }}
                        />
                    </Box>
                    <Box display="flex" alignItems={"center"} sx={{ flex: "0 0 auto", marginTop: "16px" }}>
                        <Checkbox
                            data-testid="date-checkbox"
                            checked={indefiniteTargetDate}
                            onChange={() => {
                                setIndefiniteTargetDate(!indefiniteTargetDate);
                                setEndDate("N/A");
                            }}
                        />
                        <Typography sx={{ whiteSpace: "nowrap" }}>Indefinite</Typography>
                    </Box>
                </Box>
                {/* rule name */}
                <Box display="flex" flexDirection="column" sx={{ width: "100%", height: "50px", gap: "4px" }}>
                    <Typography className="setting_section_input_label">Name {ASTERISK_ELEMENT}</Typography>
                    <CqaFormTextInput
                        data-testid="qarule-name-input"
                        id="qarule-name-input"
                        placeholder="enter name"
                        value={ruleName}
                        onChange={(event) => {
                            setName(event.target.value);
                        }}
                        sx={{
                            height: "32px",
                            angle: "0 deg",
                            opacity: 1,
                            borderRadius: "4px",
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            "& .MuiInputBase-input::placeholder": {
                                color: theme.palette.artisticTextLow.main,
                                opacity: 1,
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: "16px",
                            }
                        }}
                    />
                </Box>
                {/* description */}
                <Box display="flex" flexDirection="column" sx={{ width: "100%", height: "98px", gap: "4px" }}>
                    <Typography className="setting_section_input_label">Description</Typography>
                    <CqaFormTextArea
                        data-testid="qarule-description-input"
                        variant="outlined"
                        id="qarule-description-input"
                        placeholder="enter description"
                        multiline
                        value={description}
                        onChange={(event) => {
                            setDescription(event.target.value);
                        }}
                        sx={{
                            ...PLACEHOLDER_STYLE,
                        }}
                    />
                </Box>
                {/* purpose for project */}
                {
                    isCqaSample && (
                        <Box sx={{ width: "100%", height: "50px", display: "flex", flexDirection: "row", gap: "24px", angle: "0 deg", opacity: 1, }}>
                            <Box display="flex" flexDirection="column" sx={{ flex: 1, gap: "4px" }}>
                                <Typography className="setting_section_input_label">Purpose for project {ASTERISK_ELEMENT}</Typography>
                                <Select
                                    id="qarule-sample-reason-input"
                                    displayEmpty
                                    value={sampleReason}
                                    disabled={loading}
                                    onChange={onSampleReasonClick}
                                    IconComponent={() => (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="primary" size={20} />
                                            ) : (
                                                <IconButton disableRipple>
                                                    <img src={ChevronDown} alt="chevron-down" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                "& .MuiList-root": {
                                                    width: "100%",
                                                },
                                            }
                                        },
                                    }}
                                    sx={{
                                        ...DROPDOWN_STYLE,
                                        width: "100%",
                                        height: "32px",
                                        borderRadius: "3px",

                                    }}
                                    renderValue={(_) => {
                                        if (!sampleReason) {
                                            return (
                                                <div
                                                    style={{ color: theme.palette.artisticTextLow.main }}
                                                >
                                                    please select
                                                </div>
                                            );
                                        }

                                        return <div>{sampleReason}</div>;
                                    }}
                                >
                                    {sampleReasons.map((option) => (
                                        <MenuItem
                                            key={option.Reason}
                                            value={option.Reason}
                                            sx={{ padding: "8px 16px" }}
                                        >
                                            {option.Reason}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box display="flex" flexDirection="column" sx={{ flex: 1, gap: "4px" }}>
                                <Typography className="setting_section_input_label">Guidelines {ASTERISK_ELEMENT}</Typography>
                                <Dropdown
                                    options={availableGuidelines}
                                    selectedValue={guideline}
                                    onChange={setGuideline}
                                    placeholder="please select"
                                    loading={loading}
                                    menuListStyle={{}}
                                    paperPropsStyle={{}}
                                    sx={{ ...DROPDOWN_STYLE, width: "100%" }}
                                />
                            </Box>
                        </Box>
                    )
                }
                {/* rubric */}
                {
                    isCqaSample && (
                        <Box sx={{ width: "100%", height: "50px", display: "flex", flexDirection: "row", gap: "24px", angle: "0 deg", opacity: 1, }}>
                            <Box display="flex" flexDirection="column" sx={{ flex: 1, gap: "4px" }}>
                                <Typography className="setting_section_input_label">Workflow Collection {ASTERISK_ELEMENT}</Typography> 
                                <Select                                    
                                    data-testid="qarule-rubric-input"
                                    id="qarule-rubric-input"
                                    displayEmpty
                                    value={selectedRubricId ?? ""}
                                    disabled={loading}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSelectedRubricId(value === "" ? null : Number(value));
                                        setSelectedRubricVersionId(null); // Clear version when rubric changes
                                    }}
                                    IconComponent={() => (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="primary" size={20} />
                                            ) : (
                                                <IconButton disableRipple>
                                                    <img src={ChevronDown} alt="chevron-down" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                    sx={{
                                        ...DROPDOWN_STYLE,
                                        width: "100%",
                                        height: "32px",
                                        borderRadius: "3px",
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                "& .MuiList-root": {
                                                    width: "100%",
                                                },
                                            }
                                        },
                                    }}
                                    renderValue={(_) => {
                                        if (!selectedRubricId) {
                                            return (
                                                <div style={{ color: theme.palette.artisticTextLow.main }}>
                                                    please select
                                                </div>
                                            );
                                        }
                                        const rubric = rubrics.find(r => r.id === selectedRubricId);
                                        return <div>{rubric?.name ?? ""}{rubric && !rubric.active && " (Inactive)"}</div>;
                                    }}
                                >
                                    <MenuItem value="" sx={{ padding: "8px 16px" }}>
                                        <em>None</em>
                                    </MenuItem>
                                    {rubrics.map((rubric) => (
                                        <MenuItem
                                            key={rubric.id}
                                            value={rubric.id}
                                            disabled={!rubric.active}
                                            sx={{ padding: "8px 16px" }}
                                        >
                                            {rubric.name}{!rubric.active && " (Inactive)"}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Box display="flex" flexDirection="column" sx={{ flex: 1, gap: "4px" }}>
                                <Typography className="setting_section_input_label">Workflow Collection version {ASTERISK_ELEMENT}</Typography>
                                <Select
                                    data-testid="qarule-rubric-version-input"
                                    id="qarule-rubric-version-input"
                                    displayEmpty
                                    value={selectedRubricVersionId ?? ""}
                                    disabled={loading || !selectedRubricId}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSelectedRubricVersionId(value === "" ? null : Number(value));
                                    }}
                                    IconComponent={() => (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="primary" size={20} />
                                            ) : (
                                                <IconButton disableRipple>
                                                    <img src={ChevronDown} alt="chevron-down" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                    sx={{
                                        ...DROPDOWN_STYLE,
                                        width: "100%",
                                        height: "32px",
                                        borderRadius: "3px",
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                "& .MuiList-root": {
                                                    width: "100%",
                                                },
                                            }
                                        },
                                    }}
                                    renderValue={(_) => {
                                        if (!selectedRubricVersionId) {
                                            return (
                                                <div>
                                                    {selectedRubricId ? "please select" : "select workflow collection first"}
                                                </div>
                                            );
                                        }
                                        const version = visibleRubricVersions.find(v => v.id === selectedRubricVersionId);
                                        return <div>{version ? formatRubricVersionLabel(version) : ""}</div>;
                                    }}
                                >
                                    <MenuItem value="" sx={{ padding: "8px 16px" }}>
                                        <em>None</em>
                                    </MenuItem>
                                    {visibleRubricVersions.map((version) => (
                                        <MenuItem
                                            key={version.id}
                                            value={version.id}
                                            disabled={version.active === false || version.status !== "PUBLISHED"}
                                            sx={{ padding: "8px 16px" }}
                                        >
                                            {formatRubricVersionLabel(version)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    )
                }
            </Box>
        </Box>
    );
}
